import { useRef } from "react";
import type { ChangeEvent, CompositionEvent } from "react";

type InputLike = HTMLInputElement | HTMLTextAreaElement;

/**
 * 한글(IME) 조합 안전 입력 — 타블렛·폰 온스크린 키보드에서 글자가 통째로 사라지는 것을 막는다.
 *
 * 왜: onChange 에서 값을 손봐(toUpperCase 등) 되먹이면, 조합 중이던 글자가 IME 버퍼와 어긋나 날아간다.
 *   데스크톱에선 잘 안 드러나고 타블렛·폰 한글 자판에서 난다 — 「기기가 이상한가」로 오인된다.
 *   (2026-06-17 data-class 타블렛 수업 사고 · 해법 원본 = data-class `client/src/lib/ime.ts`)
 *
 * 규칙: 조합 중(compositionStart~End)에는 손대지 않고 raw 만 반영한다. 조합이 끝나면 한 번 변환한다.
 * 위험 패턴은 마스터 레포 `shared/qa/checks/ime-input-guard.mjs` 가 정적으로 잡는다.
 */
export function imeEmitValue(raw: string, composing: boolean, transform?: (value: string) => string) {
  return composing || !transform ? raw : transform(raw);
}

export function useImeSafeInput(
  onValue: (value: string) => void,
  transform?: (value: string) => string,
) {
  const composingRef = useRef(false);
  // Chrome 등은 compositionEnd 직후 같은 raw 로 onChange 를 한 번 더 보낸다 — 그 1회를 건너뛴다.
  const skipNextRawRef = useRef<string | null>(null);
  const emit = (raw: string) => {
    onValue(imeEmitValue(raw, composingRef.current, transform));
  };
  return {
    onChange: (event: ChangeEvent<InputLike>) => {
      const raw = event.target.value;
      if (skipNextRawRef.current !== null) {
        const skip = skipNextRawRef.current;
        skipNextRawRef.current = null;
        if (skip === raw) return;
      }
      emit(raw);
    },
    onCompositionStart: () => {
      composingRef.current = true;
      skipNextRawRef.current = null;
    },
    onCompositionEnd: (event: CompositionEvent<InputLike>) => {
      composingRef.current = false;
      const raw = (event.target as InputLike).value;
      skipNextRawRef.current = raw;
      emit(raw);
    },
  };
}
